import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { SKUList, SKUForm, SKUDetails, SKUStats } from "@/components/sku";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Package, 
  Search, 
  Filter, 
  Plus, 
  Download,
  Upload,
  BarChart3,
  Settings,
  Archive,
  RefreshCw
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const SKUManagement = () => {
  const { state, actions } = useProcurement();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingSKU, setEditingSKU] = useState<number | null>(null);
  const [viewingSKU, setViewingSKU] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState("list");
  
  // Undo functionality
  const [deletedSKU, setDeletedSKU] = useState<any>(null);
  const [undoTimeout, setUndoTimeout] = useState<NodeJS.Timeout | null>(null);
  const [undoCountdown, setUndoCountdown] = useState(0);

  // FIX: Added fetchSKUs in useEffect to load SKU data on mount
  useEffect(() => {
    const loadSKUs = async () => {
      try {
        console.log('Loading SKUs...', state.skus.length);
        await actions.fetchSKUs();
        console.log('SKUs loaded:', state.skus.length);
      } catch (error) {
        console.error('Failed to load SKUs:', error);
        toast.error('Failed to load SKU data');
      }
    };
    
    // Only fetch if we don't have SKUs yet
    if (state.skus.length === 0) {
      loadSKUs();
    }
  }, []); // Empty dependency array - only run once on mount

  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeout) {
        clearTimeout(undoTimeout);
      }
    };
  }, [undoTimeout]);


  // Filter SKUs based on search and filters with defensive programming
  const filteredSKUs = (state.skus || []).filter(sku => {
    const matchesSearch = sku.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sku.skuCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sku.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || sku.category === selectedCategory;
    
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && sku.isActive) ||
                         (statusFilter === "inactive" && !sku.isActive) ||
                         (statusFilter === "low-stock" && sku.currentStock <= sku.reorderLevel);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Get unique categories for filter with defensive programming
  const categories = Array.from(new Set((state.skus || []).map(sku => sku.category).filter(Boolean)));

  // Handle SKU creation/update
  const handleSaveSKU = async (skuData: any): Promise<boolean> => {
    const success = editingSKU 
      ? await actions.updateSKU(editingSKU, skuData)
      : await actions.createSKU(skuData);
    
    if (success) {
      setShowForm(false);
      setEditingSKU(null);
      // No need to fetchSKUs() - the context already updated the state
    }
    
    return success;
  };

  // Handle SKU deletion
  const handleDeleteSKU = async (id: number) => {
    // Store the SKU data before deletion for undo with defensive programming
    const skuToDelete = (state.skus || []).find(sku => sku.id === id);
    if (!skuToDelete) return;

    // Delete the SKU
    const success = await actions.deleteSKU(id);
    if (success) {
      // Set up undo functionality
      setDeletedSKU(skuToDelete);
      setUndoCountdown(15); // 15 seconds countdown
      
      // Start countdown
      const countdownInterval = setInterval(() => {
        setUndoCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setDeletedSKU(null);
            setUndoTimeout(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Set timeout to clear undo after 15 seconds
      const timeout = setTimeout(() => {
        setDeletedSKU(null);
        setUndoCountdown(0);
        clearInterval(countdownInterval);
      }, 15000);
      
      setUndoTimeout(timeout);
    }
  };

  const handleUndoDelete = async () => {
    if (deletedSKU) {
      // Restore the SKU
      const success = await actions.createSKU(deletedSKU);
      if (success) {
        // Clear undo state
        setDeletedSKU(null);
        setUndoCountdown(0);
        if (undoTimeout) {
          clearTimeout(undoTimeout);
          setUndoTimeout(null);
        }
        toast.success('SKU restored successfully');
      }
    }
  };

  const handleCancelUndo = () => {
    setDeletedSKU(null);
    setUndoCountdown(0);
    if (undoTimeout) {
      clearTimeout(undoTimeout);
      setUndoTimeout(null);
    }
  };

  // Handle bulk operations
  const handleBulkExport = () => {
    const csvContent = [
      ['SKU Code', 'Name', 'Description', 'Category', 'Unit', 'Current Stock', 'Reorder Level', 'Status'],
      ...filteredSKUs.map(sku => [
        sku.skuCode,
        sku.name,
        sku.description,
        sku.category,
        sku.unit,
        sku.currentStock.toString(),
        sku.reorderLevel.toString(),
        sku.isActive ? 'Active' : 'Inactive'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skus-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('SKUs exported successfully');
  };

  const handleBulkImport = () => {
    // Simulate bulk import
    toast.info('Bulk import feature coming soon');
  };

  const handleRefreshData = async () => {
    await actions.refreshMockData();
  };

  // Calculate statistics
  const stats = {
    total: state.skus.length,
    active: state.skus.filter(sku => sku.isActive).length,
    lowStock: state.skus.filter(sku => sku.currentStock <= sku.reorderLevel).length,
    categories: categories.length
  };

  // Show loading state if data is being fetched
  if (state.isLoading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading SKUs...</p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Show no data state if no SKUs are available
  if (!state.isLoading && state.skus.length === 0) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No SKUs Available</h3>
              <p className="text-muted-foreground mb-4">No SKUs found. Create your first SKU to get started.</p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First SKU
              </Button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">SKU Management</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Manage your inventory SKUs and stock levels</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleBulkExport} className="flex-1 sm:flex-none">
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Export</span>
              <span className="xs:hidden">Export</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleBulkImport} className="flex-1 sm:flex-none">
              <Upload className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Import</span>
              <span className="xs:hidden">Import</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefreshData} className="flex-1 sm:flex-none">
              <RefreshCw className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Refresh</span>
              <span className="xs:hidden">Refresh</span>
            </Button>
            <Button onClick={() => setShowForm(true)} className="flex-1 sm:flex-none">
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Add SKU</span>
              <span className="xs:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Undo Notification */}
        {deletedSKU && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-orange-800">
                  SKU "{deletedSKU.name}" deleted
                </p>
                <p className="text-xs text-orange-600">
                  Undo available for {undoCountdown} seconds
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleUndoDelete}
                className="text-orange-700 border-orange-300 hover:bg-orange-100"
              >
                Undo
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelUndo}
                className="text-orange-600 hover:bg-orange-100"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Total SKUs</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 flex-shrink-0" />
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Active</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Filter className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 flex-shrink-0" />
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Low Stock</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.lowStock}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
                <div className="ml-3 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">Categories</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.categories}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="list">SKU List</TabsTrigger>
            <TabsTrigger value="stats">Statistics</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-4">
            {/* Filters and Search */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filters & Search
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search SKUs by name, code, or description..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(category => (
                          <SelectItem key={category} value={category}>{category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="low-stock">Low Stock</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategory("all");
                        setStatusFilter("all");
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SKU List */}
            <SKUList
              skus={filteredSKUs}
              onEdit={(id) => {
                setEditingSKU(id);
                setShowForm(true);
              }}
              onView={(id) => setViewingSKU(id)}
              onDelete={handleDeleteSKU}
              isLoading={state.isLoading}
            />
          </TabsContent>

          <TabsContent value="stats">
            <SKUStats skus={state.skus} />
          </TabsContent>

          <TabsContent value="archived">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5" />
                  Archived SKUs
                </CardTitle>
                <CardDescription>
                  View and restore archived SKUs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No archived SKUs found</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* SKU Form Modal */}
        {showForm && (
          <SKUForm
            sku={editingSKU ? state.skus.find(s => s.id === editingSKU) : undefined}
            onSave={handleSaveSKU}
            onCancel={() => {
              setShowForm(false);
              setEditingSKU(null);
            }}
          />
        )}

        {/* SKU Details Modal */}
        {viewingSKU && (
          <SKUDetails
            skuId={viewingSKU}
            onClose={() => setViewingSKU(null)}
            onEdit={(id) => {
              setViewingSKU(null);
              setEditingSKU(id);
              setShowForm(true);
            }}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default SKUManagement;